import { useAuth0 } from "@auth0/auth0-react";
import { Box } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import _ from "lodash";
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import LatestReleaseModal from "views/ReleaseNotesManagment/release-note-modal";
import logoH from "./assets/logos/logoH.png";
import logoHaistack from "./assets/logos/logoHaistack.png";
import { AuthenticationGuard } from "./authentication-guard";
import {
  Notification,
  PageLoader,
  SuspenseLoader,
  TopNav
} from "./common/components";
import { APP_ROUTES } from "./common/constants/ROUTES";
import "./common/scss/global.scss";
import { sec } from "./security";
import { useImpersonateFirmMutation } from "./services/impersonate/impersonateApi";
import { setCurrentFirm } from "./services/impersonate/impersonateSlice";
import { setupLogin } from "./services/login/loginService";
import { setLoginPermissions } from "./services/permissions/permissionsSlice";
import { getUserData } from "./services/user/userSlice";
import { useAppDispatch, useAppSelector } from "./stateManagement/hooks";
import haistackTheme from "./themes/haistack";
import { clearAllApiCaches } from "./utils/cacheUtils";

const NotFound = lazy(() => import("views/404"));
const AccessDenied = lazy(() => import("views/AccessDenied"));
const Attorneys = lazy(() => import("views/Attorneys"));
const AttorneysV2 = lazy(() => import("views/v2/Attorneys"));
const CandidatesManagement = lazy(() => import("views/CandidatesManagement"));
const CandidatesManagementV2 = lazy(
  () => import("views/v2/CandidatesManagement")
);
const Employees = lazy(() => import("views/Employees"));
const EmployeesV2 = lazy(() => import("views/v2/Employees"));
const Firm = lazy(() => import("views/Firm"));
const FirmV2 = lazy(() => import("views/v2/Firm"));
const Jobs = lazy(() => import("views/Jobs"));
const JobsV2 = lazy(() => import("views/v2/Jobs"));
const AddNewJob = lazy(() => import("views/Jobs/components/AddNewJob"));
const AddNewJobV2 = lazy(() => import("views/v2/Jobs/components/AddNewJob"));
const EditJob = lazy(() => import("views/Jobs/components/EditJob"));
const EditJobV2 = lazy(() => import("views/v2/Jobs/components/EditJob"));
const Job = lazy(() => import("views/Jobs/Job"));
const JobV2 = lazy(() => import("views/v2/Jobs/Job"));
const JobPublic = lazy(() => import("views/Jobs/JobPublic"));
const JobPublicV2 = lazy(() => import("views/v2/Jobs/JobPublic"));
const NoUser = lazy(() => import("views/NoUser"));
const UserManagement = lazy(() => import("views/UserManagement"));
const UserSettings = lazy(() => import("views/UserSettings"));
const ReleaseNotesCreate = lazy(
  () => import("views/ReleaseNotesManagment/ReleaseNotesCreate")
);
const ReleaseNotesManagment = lazy(() => import("views/ReleaseNotesManagment"));
const ReleaseNotesEdit = lazy(
  () => import("views/ReleaseNotesManagment/ReleaseNotesEdit")
);
const FirmManagement = lazy(() => import("views/FirmManagement"));

export function App() {
  const v2Enabled = process.env.REACT_APP_V2_ENABLED === "true";
  const { user, isAuthenticated, logout, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isPublic = location.pathname.includes("public");

  const [showReleaseNote, setShowReleaseNote] = useState(false);
  const [bootstrapReady, setBootstrapReady] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<unknown>(null);
  const [lastImpersonatedId, setLastImpersonatedId] = useState<
    number | string | null
  >(null);

  // Prevent double-run in React 18 StrictMode (dev) and during re-mounts
  const ranBootstrap = useRef(false);

  const { isErrorMe, errorCode } = useAppSelector((state) => state.user);
  const [impersonateFirmMutation] = useImpersonateFirmMutation();

  sec.setAccessTokenSilently(getAccessTokenSilently);

  // ===== Bootstrap: token → /me → set perms/currentFirm → (maybe) login_as → ready
  useEffect(() => {
    if (!isAuthenticated) {
      setBootstrapReady(true);
      return;
    }
    if (ranBootstrap.current) return; // guard against StrictMode double-invoke
    ranBootstrap.current = true;

    let cancelled = false;

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        if (cancelled) return;

        const res = await dispatch(getUserData({ accessToken: token }));
        const { user: newUser } = (res.payload ?? {}) as { user?: any };
        if (!newUser) throw new Error("Missing /me payload");

        const role = _.get(newUser, "role", null);
        const { permissions } = setupLogin(role);

        dispatch(
          setLoginPermissions({
            permissions,
            email: _.get(newUser, "email", null),
            userId: _.get(newUser, "id", null),
            firmId: _.get(newUser, "employer.id", null),
            firmName: _.get(newUser, "employer.firm", null),
            role,
            expirationDate: _.get(newUser, "trial_expires_at", null)
          })
        );

        setShowReleaseNote(
          Boolean(_.get(newUser, "show_latest_release_notes", false))
        );

        // Determine desired firm and what the server says we are impersonating as
        const serverImpersonatedId = _.get(
          newUser,
          "impersonated_employer.id",
          null
        );
        const desiredFirm =
          role === "haistack_admin" && newUser.impersonated_employer
            ? newUser.impersonated_employer
            : newUser.employer;

        if (desiredFirm) {
          dispatch(setCurrentFirm(desiredFirm));
        }

        // Only call login_as if:
        // - admin
        // - we have a desired firm id
        // - and the server is NOT already impersonated to that same id
        // - and we haven't already impersonated it locally this session
        if (
          role === "haistack_admin" &&
          desiredFirm?.id &&
          desiredFirm.id !== serverImpersonatedId &&
          desiredFirm.id !== lastImpersonatedId
        ) {
          await impersonateFirmMutation({
            id: desiredFirm.id,
            firm: desiredFirm
          }).unwrap();
          setLastImpersonatedId(desiredFirm.id);

          // ✅ Clear all RTK Query caches to prevent stale data from previous firm
          clearAllApiCaches(dispatch);

          // After a successful login_as, DO NOT refetch /me here—let the rest of the app load.
          // Your queries will use the same access token; the server session is now scoped.
        } else {
          // We are already impersonated to the desired firm (or not admin) — skip login_as
          setLastImpersonatedId(desiredFirm?.id ?? null);
        }

        if (!cancelled) setBootstrapReady(true);
      } catch (e) {
        if (!cancelled) {
          console.error("Bootstrap failed:", e);
          setBootstrapError(e);
          // You might route to an error page instead
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    getAccessTokenSilently,
    dispatch,
    impersonateFirmMutation,
    lastImpersonatedId
  ]);

  // ===== Hard gate: while authenticated and bootstrap not done, block app
  if (isAuthenticated && !bootstrapReady) {
    return (
      <div className="absolute top-0 flex justify-center h-full w-full bg-default-background">
        <PageLoader />
      </div>
    );
  }

  // ===== Optional post-bootstrap API error handling
  if (isAuthenticated && isErrorMe) {
    switch (errorCode) {
      case 1004:
        return (
          <Suspense fallback={<SuspenseLoader />}>
            <NoUser />
          </Suspense>
        );
      case 1002:
        return (
          <Suspense fallback={<SuspenseLoader />}>
            <AccessDenied />
          </Suspense>
        );
      default:
        return (
          <Suspense fallback={<SuspenseLoader />}>
            <NoUser />
          </Suspense>
        );
    }
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={haistackTheme}>
        <CssBaseline />
        <Suspense fallback={<SuspenseLoader />}>
          <Box className="bg-default-background min-h-screen">
            {isAuthenticated && bootstrapReady && (
              <>
                <TopNav
                  isLogged={isAuthenticated && !isPublic}
                  tabs={[
                    {
                      id: "nav-firm",
                      label: "Firm",
                      name: "firm",
                      path: APP_ROUTES.FIRM
                    },
                    {
                      id: "nav-jobs",
                      label: "Jobs",
                      name: "jobs",
                      path: APP_ROUTES.JOBS
                    },
                    {
                      id: "nav-attorneys",
                      label: "Search Candidates",
                      name: "attorneys",
                      path: APP_ROUTES.ATTORNEYS
                    },
                    {
                      id: "nav-retention",
                      label: "Retention",
                      name: "retention",
                      path: APP_ROUTES.RETENTION
                    }
                  ]}
                  logo={logoH}
                  offlineLogo={logoHaistack}
                  avatar={user?.picture ?? ""}
                  withSearch={false}
                  onLogoutClick={() => {
                    localStorage.clear();
                    logout({ returnTo: `${window.location.origin}` });
                  }}
                />
                <LatestReleaseModal
                  open={showReleaseNote}
                  onClose={() => setShowReleaseNote(false)}
                />
              </>
            )}

            <Notification />

            <Routes>
              {/* Public Routes */}
              <Route path="*" element={<NotFound />} />
              <Route path={APP_ROUTES.NOT_FOUND} element={<NotFound />} />
              <Route
                path={APP_ROUTES.ACCESS_DENIED}
                element={<AccessDenied />}
              />
              <Route path={APP_ROUTES.NO_USER} element={<NoUser />} />
              {v2Enabled ? (
                <Route path={APP_ROUTES.JOB_PUBLIC} element={<JobPublicV2 />} />
              ) : (
                <Route path={APP_ROUTES.JOB_PUBLIC} element={<JobPublic />} />
              )}

              {/* Authenticated Routes */}
              {v2Enabled ? (
                <Route
                  path="/"
                  element={<AuthenticationGuard component={FirmV2} />}
                />
              ) : (
                <Route
                  path="/"
                  element={<AuthenticationGuard component={Firm} />}
                />
              )}
              <Route
                path={APP_ROUTES.USER_SETTINGS}
                element={<AuthenticationGuard component={UserSettings} />}
              />
              <Route
                path={APP_ROUTES.USER_MANAGEMENT}
                element={<AuthenticationGuard component={UserManagement} />}
              />
              {v2Enabled ? (
                <Route
                  path={APP_ROUTES.CANDIDATES_MANAGEMENT}
                  element={
                    <AuthenticationGuard component={CandidatesManagementV2} />
                  }
                />
              ) : (
                <Route
                  path={APP_ROUTES.CANDIDATES_MANAGEMENT}
                  element={
                    <AuthenticationGuard component={CandidatesManagement} />
                  }
                />
              )}
              {v2Enabled ? (
                <Route
                  path={APP_ROUTES.FIRM}
                  element={<AuthenticationGuard component={FirmV2} />}
                />
              ) : (
                <Route
                  path={APP_ROUTES.FIRM}
                  element={<AuthenticationGuard component={Firm} />}
                />
              )}
              {v2Enabled ? (
                <Route
                  path={APP_ROUTES.ATTORNEYS}
                  element={<AuthenticationGuard component={AttorneysV2} />}
                />
              ) : (
                <Route
                  path={APP_ROUTES.ATTORNEYS}
                  element={<AuthenticationGuard component={Attorneys} />}
                />
              )}
              {v2Enabled ? (
                <Route
                  path={APP_ROUTES.JOBS}
                  element={<AuthenticationGuard component={JobsV2} />}
                />
              ) : (
                <Route
                  path={APP_ROUTES.JOBS}
                  element={<AuthenticationGuard component={Jobs} />}
                />
              )}
              {v2Enabled ? (
                <Route
                  path={APP_ROUTES.JOB}
                  element={<AuthenticationGuard component={JobV2} />}
                />
              ) : (
                <Route
                  path={APP_ROUTES.JOB}
                  element={<AuthenticationGuard component={Job} />}
                />
              )}
              {v2Enabled ? (
                <Route
                  path={APP_ROUTES.JOB_PUBLIC_DEMO}
                  element={<AuthenticationGuard component={JobV2} />}
                />
              ) : (
                <Route
                  path={APP_ROUTES.JOB_PUBLIC_DEMO}
                  element={<AuthenticationGuard component={Job} />}
                />
              )}
              {v2Enabled ? (
                <Route
                  path={APP_ROUTES.ADD_NEW_JOB}
                  element={<AuthenticationGuard component={AddNewJobV2} />}
                />
              ) : (
                <Route
                  path={APP_ROUTES.ADD_NEW_JOB}
                  element={<AuthenticationGuard component={AddNewJob} />}
                />
              )}
              {v2Enabled ? (
                <Route
                  path={APP_ROUTES.EDIT_JOB}
                  element={<AuthenticationGuard component={EditJobV2} />}
                />
              ) : (
                <Route
                  path={APP_ROUTES.EDIT_JOB}
                  element={<AuthenticationGuard component={EditJob} />}
                />
              )}
              {v2Enabled ? (
                <Route
                  path={APP_ROUTES.RETENTION}
                  element={<AuthenticationGuard component={EmployeesV2} />}
                />
              ) : (
                <Route
                  path={APP_ROUTES.RETENTION}
                  element={<AuthenticationGuard component={Employees} />}
                />
              )}
              <Route
                path={APP_ROUTES.RELEASE_NOTES_CREATE}
                element={<AuthenticationGuard component={ReleaseNotesCreate} />}
              />
              <Route
                path={APP_ROUTES.RELEASE_NOTES}
                element={
                  <AuthenticationGuard component={ReleaseNotesManagment} />
                }
              />
              <Route
                path={APP_ROUTES.RELEASE_NOTES_EDIT}
                element={<AuthenticationGuard component={ReleaseNotesEdit} />}
              />
              <Route
                path={APP_ROUTES.FIRM_MANAGEMENT}
                element={<AuthenticationGuard component={FirmManagement} />}
              />
            </Routes>
          </Box>
        </Suspense>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
